import { configureStore, createSlice } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { StateInterface } from '../interfaces/interfaces';
import {
    getFilterPlaceholderText,
    getArtistsData,
    ITEMS_PER_PAGE_1,
    refresh
} from '../services/artists-page-provider';
import { saveToStorage } from '../services/artists-page-storage-provider';
import { generateDeepLink, updateDeepLink } from '../services/artists-deep-link-provider';

/**
 * initial state
 */
const initialState: StateInterface = {
    category: {
        key:0,
        value:0,
        text: 'All Artists'
    },
    artistsInitial:getArtistsData(),
    artists: getArtistsData(),

    // filter
    filterTerm: '',
    filterProperty: 'artistName',
    filterPlaceholderText: getFilterPlaceholderText(),

    // pagination
    pagination: null,
    currentPage: 0,
    itemsPerPage: ITEMS_PER_PAGE_1,
    paginationRange: 10,

    // theme (layout)
    layoutMode: 'grid', // grid|list

    // counters
    localTime: (new Date()).getTime(),
    showLoginPage: false,
    };

// the main slice
const rootSlice = createSlice({
    name: 'root',
    initialState,
    reducers: {
        main(state, action) {
            return {
                ...state,
                ...action.payload,
            }
        },

        refresh(state, action) {

            const updatedState =  {
                ...state,
                ...action.payload,
            };

            const res = refresh(updatedState);
            updatedState.artists = res.artists;
            updatedState.pagination = res.pagination;

            return updatedState;
        },

        refreshAndStorage(state, action) {

            const updatedState =  {
                ...state,
                ...action.payload,
            };

            const res = refresh(updatedState);
            updatedState.artists = res.artists;
            updatedState.pagination = res.pagination;

            // update storage
            saveToStorage(updatedState);

            return updatedState;
        },

        refreshAndStorageDeepLink(state, action) {

            const updatedState =  {
                ...state,
                ...action.payload,
            };

            const res = refresh(updatedState);
            updatedState.artists = res.artists;
            updatedState.pagination = res.pagination;

            const deepLink = generateDeepLink(
                updatedState.currentPage,
                updatedState.itemsPerPage,
                updatedState.layoutMode,
                updatedState.filterTerm
            );

            updateDeepLink(deepLink);
            saveToStorage(updatedState);

            return updatedState;
        },
    }
});

const store = configureStore({
    reducer: rootSlice.reducer,
})

export const rootActions = rootSlice.actions;
export default store;

/**
 * Infer the `RootState` and `AppDispatch` types from the store itself
 * https://redux.js.org/recipes/usage-with-typescript
 */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
// https://redux.js.org/recipes/usage-with-typescript
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector